import { FileText, Pencil, Plus, Sparkles, Trash2, Wand2 } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Page, PageHeader } from '../components/layout/AppShell';
import { ConfirmDialog, Modal, useToast } from '../components/ui/overlays';
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Field,
  Input,
  LoadingBlock,
  Select,
  Switch,
  Textarea,
} from '../components/ui/primitives';
import { useDeleteTemplate, useLeadTypes, useSaveTemplate, useTemplates } from '../hooks/queries';
import type { EmailTemplate, EmailTemplateKind } from '../types/api';

/**
 * Templates are the outreach agent's brief for the shape of a message. Two kinds, because the
 * useful thing to hand over is sometimes a skeleton and sometimes just the rules.
 */
const KINDS: Array<{
  value: EmailTemplateKind;
  label: string;
  blurb: string;
  icon: typeof FileText;
}> = [
  {
    value: 'STRUCTURE',
    label: 'Subject + body',
    blurb: 'A skeleton the agent follows and personalises per lead.',
    icon: FileText,
  },
  {
    value: 'PROMPT',
    label: 'AI instructions',
    blurb: 'Rules only. The agent writes the whole email, subject included.',
    icon: Wand2,
  },
];

const EXAMPLES: Record<EmailTemplateKind, { name: string; subject: string; body: string }> = {
  STRUCTURE: {
    name: 'Partner first touch',
    subject: 'a referral idea for {{company}}',
    body: `Hi {{firstName}},

[one sentence showing you understand what they actually do — from their site, not a guess]

Your customers [the specific moment where they need to rent something]. We handle exactly that on
RentX, and we pay a referral fee on anything that comes from you.

[one line on the concrete first step: a pilot in one location, a link on one page, a shared offer]

Is that worth 20 minutes, or would a one-pager be more useful first?`,
  },
  PROMPT: {
    name: 'Creator angle',
    subject: '',
    body: `Write as one person who watched their work to another, never as a brand.

Open with the specific video, post or issue and what was actually good about it — one sentence, no
flattery. Then the offer in plain words: a commission on every booking from their link, no
integration, live the same day.

Rules:
- Under 120 words. One idea, one ask.
- Never say "collaboration", "synergy" or "reach out".
- Name a number if you have one, otherwise offer to send the commission structure.
- Close on a question that is easy to answer in one line.
- If you cannot find something specific about them, skip the lead rather than write filler.`,
  },
};

const BLANK = {
  kind: 'STRUCTURE' as EmailTemplateKind,
  name: '',
  subject: '',
  body: '',
  notes: '',
  leadTypeId: '',
  isDefault: false,
};

type Draft = typeof BLANK & { id?: string };

const kindMeta = (kind: EmailTemplateKind) => KINDS.find((entry) => entry.value === kind) ?? KINDS[0];

export const TemplatesPage = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { data: templates, isLoading } = useTemplates(projectId);
  const { data: leadTypes } = useLeadTypes(projectId);
  const saveTemplate = useSaveTemplate(projectId ?? '');
  const deleteTemplate = useDeleteTemplate(projectId ?? '');
  const toast = useToast();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EmailTemplate | null>(null);

  const openEdit = (template: EmailTemplate) =>
    setDraft({
      id: template.id,
      kind: template.kind,
      name: template.name,
      subject: template.subject ?? '',
      body: template.body,
      notes: template.notes ?? '',
      leadTypeId: template.leadTypeId ?? '',
      isDefault: template.isDefault,
    });

  const isPrompt = draft?.kind === 'PROMPT';

  /** Fills only the empty fields, so it can never eat something half-written. */
  const useExample = () => {
    if (!draft) return;
    const example = EXAMPLES[draft.kind];
    setDraft({
      ...draft,
      name: draft.name.trim() ? draft.name : example.name,
      subject: draft.subject.trim() ? draft.subject : example.subject,
      body: draft.body.trim() ? draft.body : example.body,
    });
  };

  const submit = async () => {
    if (!draft) return;
    if (!draft.name.trim()) {
      toast.error('Give the template a name');
      return;
    }
    if (draft.kind === 'STRUCTURE' && draft.subject.trim().length < 3) {
      toast.error('A subject + body template needs a subject line');
      return;
    }
    if (draft.body.trim().length < 10) {
      toast.error(
        draft.kind === 'PROMPT'
          ? 'Write the instructions the agent should follow'
          : 'Write the body skeleton the agent should follow',
      );
      return;
    }
    try {
      await saveTemplate.mutateAsync({
        id: draft.id,
        kind: draft.kind,
        name: draft.name.trim(),
        body: draft.body,
        notes: draft.notes || undefined,
        leadTypeId: draft.leadTypeId,
        isDefault: draft.isDefault,
        ...(draft.kind === 'STRUCTURE' ? { subject: draft.subject.trim() } : {}),
      });
      toast.success(draft.id ? 'Template updated' : 'Template created');
      setDraft(null);
    } catch (error) {
      toast.error(error);
    }
  };

  if (isLoading) return <LoadingBlock />;

  return (
    <Page>
      <PageHeader
        title="Email templates"
        description="What the outreach agent follows when it writes. Pick one per campaign when you queue outreach, or leave it to the best match for the lead type."
        actions={
          <Button
            variant="primary"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => setDraft({ ...BLANK })}
          >
            New template
          </Button>
        }
      />

      {templates?.length ? (
        <div className="grid gap-3 lg:grid-cols-2">
          {templates.map((template) => {
            const meta = kindMeta(template.kind);
            return (
              <Card key={template.id} className="flex flex-col">
                <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="truncate text-[15px] font-semibold text-ink">
                        {template.name}
                      </h2>
                      <Badge tone={template.kind === 'PROMPT' ? 'warning' : 'neutral'}>
                        {meta.label}
                      </Badge>
                      {template.isDefault ? <Badge tone="brand">default</Badge> : null}
                      {template.leadType ? (
                        <Badge dot={template.leadType.color}>{template.leadType.name}</Badge>
                      ) : null}
                    </div>
                    <p className="mt-1 truncate text-[13px] text-muted">
                      {template.subject ?? 'The agent writes the subject.'}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Edit ${template.name}`}
                      onClick={() => openEdit(template)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Delete ${template.name}`}
                      onClick={() => setDeleteTarget(template)}
                    >
                      <Trash2 className="h-4 w-4 text-danger" />
                    </Button>
                  </div>
                </div>

                <CardBody className="flex-1">
                  <pre className="max-h-40 overflow-hidden whitespace-pre-wrap rounded-lg bg-raised px-3 py-2 font-mono text-[11px] leading-relaxed text-muted">
                    {template.body.slice(0, 420)}
                    {template.body.length > 420 ? '…' : ''}
                  </pre>
                </CardBody>

                <div className="border-t border-line px-5 py-3 text-2xs text-faint">
                  {template.notes ? template.notes : meta.blurb}
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <EmptyState
            icon={<FileText className="h-5 w-5" />}
            title="No templates yet"
            description="Optional, but they make the emails consistent: a skeleton to follow, or just the rules to write by. Mark one as default and every campaign uses it unless you pick another."
            action={
              <Button variant="primary" onClick={() => setDraft({ ...BLANK })}>
                Create the first template
              </Button>
            }
          />
        </Card>
      )}

      <Modal
        open={Boolean(draft)}
        onClose={() => setDraft(null)}
        title={draft?.id ? 'Edit template' : 'New template'}
        description="The agent treats this as direction, not text to paste — it rewrites for every lead."
        width="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submit} loading={saveTemplate.isPending}>
              {draft?.id ? 'Save changes' : 'Create template'}
            </Button>
          </>
        }
      >
        {draft ? (
          <div className="space-y-4">
            <div>
              <span className="field-label">Kind</span>
              <div className="grid gap-2 sm:grid-cols-2">
                {KINDS.map((kind) => (
                  <button
                    key={kind.value}
                    type="button"
                    aria-pressed={draft.kind === kind.value}
                    onClick={() => setDraft({ ...draft, kind: kind.value })}
                    className={`flex gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                      draft.kind === kind.value
                        ? 'border-brand bg-brand/5'
                        : 'border-line hover:bg-raised'
                    }`}
                  >
                    <kind.icon
                      className={`mt-0.5 h-4 w-4 shrink-0 ${
                        draft.kind === kind.value ? 'text-brand' : 'text-faint'
                      }`}
                    />
                    <span className="min-w-0">
                      <span className="block text-[13px] font-medium text-ink">{kind.label}</span>
                      <span className="mt-0.5 block text-xs text-muted">{kind.blurb}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-line bg-raised">
              <div className="flex items-center justify-between gap-3 px-3 py-2">
                <span className="flex items-center gap-1.5 text-xs font-medium text-muted">
                  <Sparkles className="h-3.5 w-3.5" />
                  Example {isPrompt ? 'instructions' : 'template'}
                </span>
                <Button variant="subtle" size="sm" onClick={useExample}>
                  Use this example
                </Button>
              </div>
              <pre className="max-h-56 overflow-auto whitespace-pre-wrap border-t border-line px-3 py-2 font-mono text-[11px] leading-relaxed text-muted">
                {isPrompt ? '' : `Subject: ${EXAMPLES[draft.kind].subject}\n\n`}
                {EXAMPLES[draft.kind].body}
              </pre>
            </div>

            <Field label="Name" htmlFor="tpl-name">
              <Input
                id="tpl-name"
                autoFocus
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                placeholder={EXAMPLES[draft.kind].name}
              />
            </Field>

            {isPrompt ? null : (
              <Field
                label="Subject"
                htmlFor="tpl-subject"
                hint="Placeholders: {{firstName}}, {{company}}, {{senderName}}."
              >
                <Input
                  id="tpl-subject"
                  value={draft.subject}
                  onChange={(event) => setDraft({ ...draft, subject: event.target.value })}
                  placeholder={EXAMPLES.STRUCTURE.subject}
                />
              </Field>
            )}

            <Field
              label={isPrompt ? 'Instructions' : 'Body'}
              htmlFor="tpl-body"
              hint={
                isPrompt
                  ? 'How to write, what to open with, what never to say. The agent gets these verbatim and writes the message itself.'
                  : 'Use [square brackets] for the parts the agent must replace with something it actually found.'
              }
            >
              <Textarea
                id="tpl-body"
                rows={14}
                className="prompt-input"
                value={draft.body}
                placeholder={EXAMPLES[draft.kind].body.split('\n')[0]}
                onChange={(event) => setDraft({ ...draft, body: event.target.value })}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Lead type"
                htmlFor="tpl-leadtype"
                hint="Templates for a type win over general ones when the agent picks the best match."
              >
                <Select
                  id="tpl-leadtype"
                  value={draft.leadTypeId}
                  onChange={(event) => setDraft({ ...draft, leadTypeId: event.target.value })}
                >
                  <option value="">Any lead type</option>
                  {(leadTypes ?? []).map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="Notes" htmlFor="tpl-notes" hint="For you, not the agent.">
                <Input
                  id="tpl-notes"
                  value={draft.notes}
                  onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
                  placeholder="Keep it operator-to-operator."
                />
              </Field>
            </div>

            <div className="border-t border-line pt-4">
              <Switch
                checked={draft.isDefault}
                onChange={(value) => setDraft({ ...draft, isDefault: value })}
                label="Use as the project default"
                description="Picked whenever a campaign does not name a template and nothing matches the lead type more closely. Only one template can be the default."
              />
            </div>
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Delete template"
        destructive
        confirmLabel="Delete"
        loading={deleteTemplate.isPending}
        message={
          <>
            Delete <strong className="text-ink">{deleteTarget?.name}</strong>? Campaigns already
            queued fall back to the best match for the lead type.
          </>
        }
        onConfirm={async () => {
          if (!deleteTarget) return;
          try {
            await deleteTemplate.mutateAsync(deleteTarget.id);
            toast.success('Template deleted');
            setDeleteTarget(null);
          } catch (error) {
            toast.error(error);
          }
        }}
      />
    </Page>
  );
};
